import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Alert } from 'react-native';
import { appConfig } from '../../config/configManager';
import { UserAgreementModal } from '../UserAgreementModal';
import { API_CONSTANTS, AUTH_CONSTANTS } from '../../config/constants';
import { useProfileStore } from '../../contexts/StoreContext';
import { AppProfileStorage } from '../../services/AppProfileStorage';
import { convertAppProfileToChildProfile } from '../../types/profile';
import type { SignUpCreateAccountResponseDto } from '../../types/api';

interface AccountSetupProps {
  onBack?: () => void;
  storage?: any;
  apiService?: any;
  initialStep?: 'selection' | 'new-account' | 'existing-account';
  isInitialSetup?: boolean;
  onAccountSetupComplete?: () => void;
}

export const AccountSetupView: React.FC<AccountSetupProps> = ({ 
  onBack, 
  storage, 
  apiService,
  initialStep = 'selection',
  isInitialSetup = false,
  onAccountSetupComplete
}) => {
  const [setupStep, setSetupStep] = useState<'selection' | 'new-account' | 'existing-account' | 'verification-code' | 'referral'>(initialStep);
  const [originalAccountType, setOriginalAccountType] = useState<'new-account' | 'existing-account' | null>(initialStep !== 'selection' ? initialStep : null);
  const [newAccountData, setNewAccountData] = useState<{ isInitialSetup: boolean } | null>(null);
  const [referralNotFound, setReferralNotFound] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '', // Only used for existing account sign in
    confirmPassword: '', // Not used anymore but kept for compatibility
    verificationCode: '', // For verification code input
    referredBy: '' // Optional referral code or email
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentCredentials, setCurrentCredentials] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string>(''); // Token received from verification code send
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);

  // Store access - make conditional to avoid context errors during initial setup
  let profileStore: any = null;
  let appProfileStorage: any = null;
  
  try {
    profileStore = useProfileStore();
    appProfileStorage = storage ? new AppProfileStorage(storage) : null;
  } catch (error) {
    // Store context not available during initial setup - this is expected
  }

  // Profile sync queuing for delayed sync when stores become available
  const queueProfileSync = async (authData: any, serverProfiles: any[]) => {
    if (serverProfiles.length === 0) return;
    
    console.log('📋 AccountSetup: Queuing profile sync for delayed execution');
    console.log('   📋 Server profiles to queue:', serverProfiles.length);
    
    // Store profile sync data in temporary storage for later processing
    const profileSyncData = {
      authData,
      serverProfiles,
      timestamp: Date.now(),
      accountId: authData.user?.accountId || 'default-account'
    };
    
    try {
      await storage.setItem('pending_profile_sync', JSON.stringify(profileSyncData));
      console.log('✅ AccountSetup: Profile sync data queued successfully');
      
      // Set a flag to indicate profiles are pending sync
      await storage.setItem('profiles_pending_sync', 'true');
      console.log('✅ AccountSetup: Pending sync flag set');
    } catch (error) {
      console.error('❌ AccountSetup: Failed to queue profile sync:', error);
    }
  };

  // Check for existing credentials on mount
  useEffect(() => {
    checkExistingCredentials();
  }, []);

  const checkExistingCredentials = async () => {
    if (!storage) return;
    
    try {
      const storedEmail = await storage.getItem('user_email');
      const storedToken = await storage.getItem('auth_token');
      
      if (storedEmail) {
        setCurrentCredentials({
          email: storedEmail,
          hasToken: !!storedToken,
          lastLogin: await storage.getItem('last_login_date')
        });
        setFormData(prev => ({ ...prev, email: storedEmail }));
      }
    } catch (error) {
      console.log('Error checking existing credentials:', error);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (isNewAccount: boolean): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Both new and existing accounts now require name (no password for either)
    if (!formData.name.trim()) {
      newErrors.name = isNewAccount ? 'Name is required' : 'Account name is required';
    }
    
    return newErrors;
  };

  const handleSetupChoice = (choice: 'new-account' | 'existing-account') => {
    setSetupStep(choice);
    setOriginalAccountType(choice);
    setFormData({ name: '', email: currentCredentials?.email || '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
    setErrors({});
    setVerificationToken('');
  };

  const handleFormSubmit = async () => {
    const isNewAccount = setupStep === 'new-account';
    const isAccountExistsCode = (code: unknown): boolean => {
      const normalized = String(code ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      return normalized === 'accountexists';
    };
    const looksLikeAccountExists = (...values: unknown[]): boolean => {
      const merged = values
        .map((v) => {
          if (typeof v === 'string') return v;
          try { return JSON.stringify(v); } catch { return ''; }
        })
        .join(' ')
        .toLowerCase();
      return merged.includes('accountexists') || merged.includes('account exists');
    };
    const formErrors = validateForm(isNewAccount);
    
    setErrors(formErrors);
    
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    if (isNewAccount && !agreedToTerms) {
      setErrors({ terms: 'You must agree to the Terms of Service to create an account.' });
      return;
    }
    
    if (!storage || !apiService) {
      Alert.alert('Error', 'Storage or API service not available');
      return;
    }

    setIsLoading(true);

    try {
      // Both new and existing accounts now use verification code flow
      console.log(`📧 Sending verification code for ${isNewAccount ? 'new' : 'existing'} account...`);
      
      const verificationPayload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        accesstype: API_CONSTANTS.DEFAULT_ACCESS_TYPE,
        skintype: API_CONSTANTS.DEFAULT_SKIN_TYPE,
        signature: API_CONSTANTS.ACCOUNT_SERVICE_SIGNATURE
      };
      
      const serviceRequest = {
        RequestType: isNewAccount ? 'signup_request_verify' : 'signup_request_verify_existing',
        Payload: JSON.stringify(verificationPayload)
      };
      
      console.log('🌐 Account Service Request:');
      console.log('  - RequestType:', serviceRequest.RequestType);
      console.log('  - Payload:', serviceRequest.Payload);
      console.log(`  - Account Type: ${isNewAccount ? 'New Account' : 'Existing Account'}`);

      const serviceResponse = await apiService.accountService(serviceRequest);
      
      console.log('✅ Account service response received');
      console.log('📦 Response data:', JSON.stringify(serviceResponse, null, 2));

      let responsePayload: any = {};
      try {
        responsePayload = JSON.parse(serviceResponse?.payload || '{}');
      } catch {
        responsePayload = {};
      }

      const responseErrorCode =
        serviceResponse?.errorCode ||
        serviceResponse?.ErrorCode ||
        responsePayload?.errorCode ||
        responsePayload?.ErrorCode;

      const responseErrorText =
        serviceResponse?.message ||
        serviceResponse?.errorDescription ||
        responsePayload?.message ||
        responsePayload?.errorDescription ||
        responsePayload?.description;

      console.log('🔎 AccountSetup verify error inspection:', {
        isNewAccount,
        responseErrorCode,
        responseErrorText,
        payloadErrorCode: responsePayload?.errorCode || responsePayload?.ErrorCode,
      });

      if (isNewAccount && (isAccountExistsCode(responseErrorCode) || looksLikeAccountExists(responseErrorText, responsePayload))) {
        const accountExistsError: any = new Error('Account already exists');
        accountExistsError.errorCode = 'AccountExists';
        throw accountExistsError;
      }
      
      if (serviceResponse && serviceResponse.success) {
        // Parse the response payload to get the token
        const token = responsePayload.token || serviceResponse.result;
        if (token) {
          setVerificationToken(token);
          setSetupStep('verification-code');
        } else {
          throw new Error('No verification token received from server');
        }
      } else {
        throw new Error(serviceResponse?.message || 'Failed to send verification code');
      }
    } catch (error: any) {
      const caughtErrorCode =
        error?.errorCode ||
        error?.response?.data?.errorCode ||
        error?.response?.data?.ErrorCode;
      const isAccountExists =
        isNewAccount &&
        (
          isAccountExistsCode(caughtErrorCode) ||
          looksLikeAccountExists(
            error?.message,
            error?.response?.data,
            error?.response?.data?.payload,
            error?.response?.data?.errorDescription
          )
        );

      console.log('🔎 AccountSetup catch error inspection:', {
        isNewAccount,
        caughtErrorCode,
        errorMessage: error?.message,
        responseData: error?.response?.data,
        matchedAccountExists: isAccountExists,
      });

      console.error('❌ Verification code error details:');
      console.error('Error message:', error.message);
      if (error.response) {
        console.error('HTTP Status:', error.response.status);
        console.error('Response data:', JSON.stringify(error.response.data, null, 2));
      } else if (error.request) {
        console.error('No response received from server');
        console.error('Request details:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }

      if (isAccountExists) {
        Alert.alert(
          'Account Already Exists',
          "An account already exists for this email. Please go back and select 'Sign Into Existing Family Account'.",
          [{ text: 'OK' }]
        );
        return;
      }

      Alert.alert(
        'Verification Code Failed', 
        'Could not send verification code. Please check your information and try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    const formErrors: Record<string, string> = {};
    
    if (!formData.verificationCode.trim()) {
      formErrors.verificationCode = 'Verification code is required';
    }
    
    setErrors(formErrors);
    
    if (Object.keys(formErrors).length > 0) {
      return;
    }
    
    if (!storage || !apiService) {
      Alert.alert('Error', 'Storage or API service not available');
      return;
    }

    setIsLoading(true);

    try {
      // Use the original account type choice to determine the correct request type
      const isNewAccount = originalAccountType === 'new-account';
      
      console.log(`🏗️ ${isNewAccount ? 'Creating new account' : 'Signing into existing account'} with verification code...`);
      
      const accountPayload: Record<string, string> = {
        token: verificationToken,
        verificationCode: formData.verificationCode.trim(),
        signature: API_CONSTANTS.ACCOUNT_SERVICE_SIGNATURE
      };
      
      const serviceRequest = {
        RequestType: isNewAccount ? 'signup_create_account' : 'signup_existing_account',
        Payload: JSON.stringify(accountPayload)
      };
      
      console.log('🌐 Account Service Request:');
      console.log('  - RequestType:', serviceRequest.RequestType);
      console.log('  - Payload:', serviceRequest.Payload);
      console.log(`  - Account Type: ${isNewAccount ? 'New Account Creation' : 'Existing Account Sign In'}`);

      const serviceResponse = await apiService.accountService(serviceRequest);
      
      console.log('✅ Account service response received');
      console.log('📦 Response data:', JSON.stringify(serviceResponse, null, 2));
      
      if (serviceResponse && serviceResponse.success) {
        // Parse the response payload to get the account data
        let responsePayload: SignUpCreateAccountResponseDto;
        try {
          responsePayload = JSON.parse(serviceResponse.payload || '{}') as SignUpCreateAccountResponseDto;
        } catch (e) {
          console.error('❌ Error parsing account response payload:', e);
          throw new Error('Invalid response format from server');
        }
        
        console.log(`🎉 Account ${isNewAccount ? 'creation' : 'sign in'} successful, response payload:`, responsePayload);
        
        // Validate required fields from response
        if (!responsePayload.accountId || !responsePayload.sraId || !responsePayload.email) {
          throw new Error('Incomplete account data received from server');
        }
        
        // Store all account information with proper fallbacks
        const accountData: SignUpCreateAccountResponseDto = {
          accountId: responsePayload.accountId,
          sraId: responsePayload.sraId,
          name: responsePayload.name || formData.name,
          email: responsePayload.email || formData.email,
          password: responsePayload.password || '',
          signature: responsePayload.signature || ''
        };
        
        console.log('💾 Storing account data:', {
          accountId: accountData.accountId,
          sraId: accountData.sraId,
          name: accountData.name,
          email: accountData.email,
          hasPassword: !!accountData.password,
          hasSignature: !!accountData.signature
        });
        
        // Store basic user information
        await storage.setItem('user_email', accountData.email);
        await storage.setItem('user_name', accountData.name);
        await storage.setItem('user_sra_id', accountData.sraId);
        
        // Store secure credentials
        if (accountData.password) {
          await storage.setItem('user_password', accountData.password);
        }
        if (accountData.signature) {
          await storage.setItem('user_signature', accountData.signature);
        }
        await storage.setItem('last_login_date', new Date().toISOString());
        
        // Create comprehensive user session data
        const sessionData = {
          user: {
            id: accountData.sraId,
            email: accountData.email,
            name: accountData.name,
            accountId: accountData.accountId,
          },
          familyAccount: {
            familyId: accountData.accountId,
            familyName: `${accountData.name}'s Family`,
            accountTier: 'standard',
            subscriptionStatus: 'active'
          }
        };
        
        await storage.setItem('userSession', JSON.stringify(sessionData));
        
        console.log('💾 Account data stored successfully');
        
        // Automatically sign in with the new credentials only if we have them
        if (accountData.password && accountData.signature) {
          console.log('🔄 Attempting automatic sign in with account credentials...');
          
          try {
            const loginRequest = {
              Email: accountData.email,
              Password: accountData.password,
              Signature: accountData.signature,
              AccessType: API_CONSTANTS.DEFAULT_ACCESS_TYPE,
              SkinType: API_CONSTANTS.DEFAULT_SKIN_TYPE,
            };

            console.log('🌐 Auto-login request:');
            console.log('  - Email:', loginRequest.Email);
            console.log('  - Password:', '[HIDDEN]');
            console.log('  - Signature:', loginRequest.Signature);

            const loginResponse = await apiService.authenticate(loginRequest);
            
            console.log('✅ Auto-login response received');
            console.log('📦 Response data:', JSON.stringify(loginResponse, null, 2));
            
            if (loginResponse && loginResponse.success && loginResponse.data) {
              const authData = loginResponse.data;
              
              // Store authentication tokens
              await storage.setItem('auth_token', authData.token);
              await storage.setItem('refresh_token', authData.refreshToken);
              await storage.setItem('auth_timestamp', new Date().toISOString());
              
              // Set the auth token in API service for immediate use
              console.log('🔐 AccountSetup: Setting auth token in ApiService after account setup');
              apiService.setAuthToken(authData.token);
              
              // Perform profile sync if we have server profiles and profile store
              const serverProfiles = authData?.profiles?.profiles || [];
              console.log('🔄 AccountSetup: Profile sync check:');
              console.log('   📋 Server profiles count:', serverProfiles.length);
              console.log('   📋 Profile store available:', !!profileStore);
              console.log('   📋 App profile storage available:', !!appProfileStorage);
              
              if (serverProfiles.length > 0 && profileStore && appProfileStorage) {
                try {
                  console.log('🔄 AccountSetup: Starting profile sync process...');
                  const profileStoreState = profileStore.getState();
                  const localProfiles = profileStoreState.profiles;
                  const accountId = authData.user?.accountId || 'default-account';
                  
                  // Determine which profiles need syncing
                  const profilesToSync = [];
                  let addedCount = 0;
                  let updatedCount = 0;
                  
                  for (const serverProfile of serverProfiles) {
                    const localProfile = localProfiles.find((p: any) => p.id === serverProfile.profileId);
                    
                    if (!localProfile) {
                      console.log(`   ➕ New profile found: ${serverProfile.profileId}`);
                      profilesToSync.push({ profileId: serverProfile.profileId, reason: 'NEW' });
                      addedCount++;
                    } else {
                      // Check if server profile is newer (basic check)
                      const serverModified = new Date(serverProfile.modified);
                      const localModified = new Date(localProfile.lastSyncDate || localProfile.modified || '1970-01-01');
                      
                      if (serverModified > localModified) {
                        console.log(`   🔄 Profile needs update: ${serverProfile.profileId}`);
                        profilesToSync.push({ profileId: serverProfile.profileId, reason: 'UPDATE' });
                        updatedCount++;
                      } else {
                        console.log(`   ✅ Profile up to date: ${serverProfile.profileId}`);
                      }
                    }
                  }
                  
                  console.log(`🔄 AccountSetup: Profile sync plan: ${addedCount} new, ${updatedCount} updates`);
                  
                  // Download profiles that need syncing
                  for (const profileSync of profilesToSync) {
                    try {
                      console.log(`📥 AccountSetup: Downloading profile ${profileSync.profileId} (${profileSync.reason})`);
                      
                      // Create service request to download profile
                      const serviceRequest = {
                        RequestType: 'profile_get',
                        Payload: JSON.stringify({
                          profileId: profileSync.profileId,
                          accountId: accountId
                        })
                      };
                      
                      const profileResponse = await apiService.processServiceRequestWithRefresh(serviceRequest, 'sync', {
                        userStore: (globalThis as any).__userStore,
                        storage,
                      });
                      
                      if (profileResponse && profileResponse.success && profileResponse.payload) {
                        console.log(`✅ AccountSetup: Profile ${profileSync.profileId} downloaded successfully`);
                        
                        // Parse and save the profile using AppProfileStorage
                        const appProfileDto = JSON.parse(profileResponse.payload);
                        console.log('🔎 AccountSetup profile_get payload track counts:', {
                          profileId: appProfileDto.profileId ?? appProfileDto.ProfileId,
                          prescriptionsCamel: appProfileDto.prescriptions?.events?.length ?? 0,
                          prescriptionsPascal: appProfileDto.Prescriptions?.Events?.length ?? 0,
                          referralsCamel: appProfileDto.referrals?.events?.length ?? 0,
                          referralsPascal: appProfileDto.Referrals?.Events?.length ?? 0,
                        });
                        
                        if (appProfileStorage) {
                          await appProfileStorage.saveProfile(appProfileDto);
                          console.log(`💾 AccountSetup: Profile ${profileSync.profileId} saved to storage`);
                          
                          // Convert AppProfileDto to ChildProfile format and add to store directly
                          const childProfile = convertAppProfileToChildProfile(appProfileDto, storage);
                          
                          // Skip profiles that were deleted on the server
                          if (childProfile.profileStatus === -1) {
                            console.log(`🗑️ AccountSetup: Skipping deleted profile ${profileSync.profileId}`);
                            continue;
                          }
                          
                          // Add directly to profile store instead of using forceRehydrate
                          const profileStoreState = profileStore.getState();
                          const existingProfile = profileStoreState.profiles.find((p: any) => p.id === profileSync.profileId);
                          
                          if (existingProfile) {
                            // Update existing profile
                            profileStoreState.updateProfile(profileSync.profileId, childProfile);
                            console.log(`➕ AccountSetup: Updated existing profile ${profileSync.profileId}`);
                          } else {
                            // Add new profile — skipBackendSync=true because profile came FROM the server
                            await profileStoreState.addProfile(childProfile, { skipBackendSync: true });
                            console.log(`➕ AccountSetup: Added new profile ${profileSync.profileId} to store`);
                          }
                          
                          // Update persistence to ensure profile stays loaded
                          const updatedState = profileStore.getState();
                          if (updatedState.profiles.some((p: any) => p.id === profileSync.profileId)) {
                            console.log(`💾 AccountSetup: Profile ${profileSync.profileId} successfully loaded into store`);
                          }
                        }
                      } else {
                        console.error(`❌ AccountSetup: Failed to download profile ${profileSync.profileId}:`, profileResponse?.message);
                      }
                    } catch (profileSyncError) {
                      console.error(`❌ AccountSetup: Profile sync error for ${profileSync.profileId}:`, profileSyncError);
                    }
                  }
                  
                  console.log(`✅ AccountSetup: Profile sync completed - processed ${profilesToSync.length} profiles`);
                  
                  // Give the profile store a moment to finish rehydration before navigation
                  if (profilesToSync.length > 0) {
                    console.log('⏳ AccountSetup: Waiting for profile store stabilization...');
                    await new Promise(resolve => setTimeout(resolve, 500));
                  }
                } catch (profileSyncError) {
                  console.error('❌ AccountSetup: Profile sync failed:', profileSyncError);
                  // Don't block account setup if profile sync fails
                }
              } else {
                if (serverProfiles.length > 0 && !profileStore) {
                  console.log('🔄 AccountSetup: Store context not available - queuing profile sync for later');
                  await queueProfileSync(authData, serverProfiles);
                } else if (serverProfiles.length > 0 && !appProfileStorage) {
                  console.log('🔄 AccountSetup: Profile sync skipped - storage not available');
                } else {
                  console.log('🔄 AccountSetup: No server profiles to sync');
                }
              }
              
              if (isNewAccount) {
                setNewAccountData({ isInitialSetup: isInitialSetup && !!onAccountSetupComplete });
                setFormData(prev => ({ ...prev, verificationCode: '' }));
                setVerificationToken('');
                setSetupStep('referral');
              } else {
                if (isInitialSetup && onAccountSetupComplete) {
                  // For initial setup, go directly to main app instead of showing any dialogs
                  console.log('✅ Existing account signed in from initial setup, navigating to main app');
                  setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
                  setVerificationToken('');
                  setSetupStep('selection');
                  onAccountSetupComplete(); // Navigate to HomeView
                } else {
                  // For existing accounts from AccountView, skip dialog and go straight to AccountView
                  console.log('✅ Existing account signed in successfully, navigating to AccountView');
                  setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
                  setVerificationToken('');
                  setSetupStep('selection');
                  if (onBack) onBack(); // Navigate back to AccountView immediately
                }
              }
            } else {
              // Account setup successful but auto-login failed
              throw new Error('Auto-login failed but account setup was successful');
            }
          } catch (loginError) {
            console.error('❌ Auto-login failed after account setup:', loginError);
            
            if (isNewAccount) {
              setNewAccountData({ isInitialSetup: isInitialSetup && !!onAccountSetupComplete });
              setFormData(prev => ({ ...prev, verificationCode: '' }));
              setVerificationToken('');
              setSetupStep('referral');
            } else if (isInitialSetup && onAccountSetupComplete) {
              Alert.alert(
                'Account Verified!',
                'Account verification successful. Please sign in again from the Account section.',
                [
                  {
                    text: 'Continue',
                    onPress: () => {
                      setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
                      setVerificationToken('');
                      setSetupStep('selection');
                      onAccountSetupComplete();
                    }
                  }
                ]
              );
            } else {
              Alert.alert(
                'Account Access Granted!',
                'Account verification successful but automatic sign-in failed. Please try signing in again.',
                [
                  {
                    text: 'OK',
                    onPress: () => {
                      setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
                      setVerificationToken('');
                      setSetupStep('selection');
                      if (onBack) onBack();
                    }
                  }
                ]
              );
            }
          }
        } else {
          // Account setup successful but no credentials for auto-login
          if (isNewAccount) {
            setNewAccountData({ isInitialSetup: isInitialSetup && !!onAccountSetupComplete });
            setFormData(prev => ({ ...prev, verificationCode: '' }));
            setVerificationToken('');
            setSetupStep('referral');
          } else if (isInitialSetup && onAccountSetupComplete) {
            Alert.alert(
              'Account Verified!',
              'Account verification successful. You can now access your family account.',
              [
                {
                  text: 'Continue',
                  onPress: () => {
                    setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
                    setVerificationToken('');
                    setSetupStep('selection');
                    onAccountSetupComplete();
                  }
                }
              ]
            );
          } else {
            Alert.alert(
              'Account Verified Successfully!',
              'Account verification successful. You can now access your family account.',
              [
                {
                  text: 'OK',
                  onPress: () => {
                    setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
                    setVerificationToken('');
                    setSetupStep('selection');
                    if (onBack) onBack();
                  }
                }
              ]
            );
          }
        }
      } else {
        throw new Error(serviceResponse?.message || 'Failed to process account request');
      }
    } catch (error: any) {
      console.error('❌ Account processing error details:');
      console.error('Error message:', error.message);
      if (error.response) {
        console.error('HTTP Status:', error.response.status);
        console.error('Response data:', JSON.stringify(error.response.data, null, 2));
      } else if (error.request) {
        console.error('No response received from server');
        console.error('Request details:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      
      Alert.alert(
        'Account Processing Failed', 
        'Could not process your account request. Please check your verification code and try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCredentials = async () => {
    Alert.alert(
      'Sign Out?',
      'This will remove your stored credentials and the app will run in offline mode only.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Sign Out',
          style: 'destructive',
          onPress: async () => {
            if (!storage) return;
            
            try {
              await storage.removeItem('user_email');
              await storage.removeItem('auth_token');
              await storage.removeItem('refresh_token');
              await storage.removeItem('last_login_date');
              await storage.removeItem('userSession');
              
              // Clear auth token from API service
              if (apiService) {
                apiService.setAuthToken(null);
              }
              
              setCurrentCredentials(null);
              setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
              setVerificationToken('');
              setSetupStep('selection');
              
              Alert.alert('Success', 'Signed out successfully. App is now in offline mode.');
            } catch (error) {
              console.error('Error removing credentials:', error);
              Alert.alert('Error', 'Could not sign out');
            }
          }
        }
      ]
    );
  };

  const renderSelectionScreen = () => (
    <View style={styles.container}>
      <View style={styles.backgroundImageContainer}>
        <View style={styles.backgroundOverlay} />
      </View>
      <TouchableOpacity style={styles.backButtonTop} onPress={onBack}>
        <Text style={styles.backButtonText}>← Back</Text>
      </TouchableOpacity>
      <View style={styles.welcomeContent}>
        <View style={styles.imageSection}>
          <View style={styles.colorfulImagePlaceholder}>
            <Text style={styles.imageEmojis}>👨‍👩‍👧‍👦🔗💫</Text>
            <Text style={styles.imageEmojis}>☁️📱💻🌐</Text>
            <Text style={styles.imageEmojis}>🔒✨🎯📊</Text>
          </View>
        </View>
        
        <Text style={styles.welcomeTitle}>Account Setup</Text>
        <Text style={styles.welcomeSubtitle}>
          {currentCredentials ? 
            `Currently connected as ${currentCredentials.email}. Choose an option below:` :
            'Setup an account to sync data across devices and enable connections and sharing'
          }
        </Text>
        
        <View style={styles.optionsContainer}>
          {currentCredentials && (
            <View style={styles.currentAccountCard}>
              <Text style={styles.currentAccountIcon}>✅</Text>
              <View style={styles.currentAccountInfo}>
                <Text style={styles.currentAccountTitle}>Connected Account</Text>
                <Text style={styles.currentAccountEmail}>{currentCredentials.email}</Text>
                {currentCredentials.lastLogin && (
                  <Text style={styles.currentAccountLastLogin}>
                    Last login: {new Date(currentCredentials.lastLogin).toLocaleDateString()}
                  </Text>
                )}
              </View>
              <TouchableOpacity 
                style={styles.signOutButton}
                onPress={handleRemoveCredentials}
              >
                <Text style={styles.signOutButtonText}>Sign Out</Text>
              </TouchableOpacity>
            </View>
          )}
          
          <TouchableOpacity 
            style={styles.optionButton}
            onPress={() => handleSetupChoice('new-account')}
          >
            <Text style={styles.optionTitle}>Create New Family Account</Text>
            <Text style={styles.optionDescription}>
              Set up a new family account to share access
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.optionButton}
            onPress={() => handleSetupChoice('existing-account')}
          >
            <Text style={styles.optionTitle}>Sign Into Existing Family Account</Text>
            <Text style={styles.optionDescription}>
              {currentCredentials ? 
                'Update your credentials or sign into a different account' :
                'Already have an account or an account has been setup for you? Sign in to share access'
              }
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderFormScreen = () => {
    const isNewAccount = setupStep === 'new-account';
    const isVerificationStep = setupStep === 'verification-code';
    
    return (
      <View style={styles.container}>
        <View style={styles.backgroundImageContainer}>
          <View style={styles.backgroundOverlay} />
        </View>
        <View style={styles.formContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => {
            if (isVerificationStep) {
              setSetupStep(originalAccountType || 'new-account');
              setVerificationToken('');
              setFormData(prev => ({ ...prev, verificationCode: '' }));
              setErrors({});
            } else {
              // If user started directly in a specific step (not selection) and is still in that step,
              // go back to the previous view instead of selection step
              if (initialStep !== 'selection' && setupStep === initialStep && onBack) {
                onBack();
              } else {
                setSetupStep('selection');
                setOriginalAccountType(null);
              }
            }
          }}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
          
          <Text style={styles.formTitle}>
            {isVerificationStep ? 'Enter Verification Code' : (isNewAccount ? 'Create New Family Account' : 'Sign Into An Existing Account')}
          </Text>
          <Text style={styles.formSubtitle}>
            {isVerificationStep ? 
              `We've sent a verification code to ${formData.email}. Please enter it below to complete your account setup.` :
              (isNewAccount ? 
                'Enter your information and we\'ll send you a verification code' :
                'Signing in will disable anyone else from using the account'
              )
            }
          </Text>
          
          <View style={styles.form}>
            {isVerificationStep ? (
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Verification Code *</Text>
                <TextInput
                  style={[styles.input, errors.verificationCode ? styles.inputError : null]}
                  value={formData.verificationCode}
                  onChangeText={(text) => setFormData({ ...formData, verificationCode: text })}
                  placeholder="Enter verification code"
                  keyboardType="default"
                  autoCapitalize="characters"
                  autoCorrect={false}
                  editable={!isLoading}
                />
                {errors.verificationCode && <Text style={styles.errorText}>{errors.verificationCode}</Text>}
              </View>
            ) : (
              <>
                <View style={styles.inputContainer}>
                  <Text style={styles.inputLabel}>Email address *</Text>
                  <TextInput
                    style={[styles.input, errors.email ? styles.inputError : null]}
                    value={formData.email}
                    onChangeText={(text) => setFormData({ ...formData, email: text })}
                    placeholder={isNewAccount ? 'your.email@example.com' : 'account.email@example.com'}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!isLoading}
                  />
                  {errors.email && <Text style={styles.errorText}>{errors.email}</Text>}
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.inputLabel}>{isNewAccount ? 'Name *' : 'Your name on this account *'}</Text>
                  <TextInput
                    style={[styles.input, errors.name ? styles.inputError : null]}
                    value={formData.name}
                    onChangeText={(text) => setFormData({ ...formData, name: text })}
                    placeholder={isNewAccount ? 'Enter your name' : 'Enter your name on this account'}
                    autoCapitalize="words"
                    editable={!isLoading}
                  />
                  {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
                </View>

                {isNewAccount && (
                  <View style={styles.termsRow}>
                    <TouchableOpacity
                      style={[styles.termsCheckbox, agreedToTerms && styles.termsCheckboxChecked]}
                      onPress={() => setAgreedToTerms(v => !v)}
                      disabled={isLoading}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: agreedToTerms }}
                    >
                      {agreedToTerms && <Text style={styles.termsCheckboxMark}>✓</Text>}
                    </TouchableOpacity>
                    <Text style={styles.termsText}>
                      I agree to the{' '}
                      <Text style={styles.termsLink} onPress={() => setShowAgreement(true)}>
                        Terms of Service &amp; Privacy Policy
                      </Text>
                    </Text>
                  </View>
                )}
                {isNewAccount && errors.terms && (
                  <Text style={[styles.errorText, { marginTop: 4 }]}>{errors.terms}</Text>
                )}
              </>
            )}

            <TouchableOpacity
              style={[styles.submitButton, (isLoading || (!isVerificationStep && isNewAccount && !agreedToTerms)) && styles.submitButtonDisabled]}
              onPress={isVerificationStep ? handleCreateAccount : handleFormSubmit}
              disabled={isLoading || (!isVerificationStep && isNewAccount && !agreedToTerms)}
            >
              <Text style={styles.submitButtonText}>
                {isLoading ? 'Processing...' : 
                  (isVerificationStep ? (originalAccountType === 'new-account' ? 'Create New Account' : 'Sign Into Account') : 'Send Verification Code')
                }
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        <UserAgreementModal visible={showAgreement} onClose={() => setShowAgreement(false)} />
      </View>
    );
  };

  const completeAccountSetup = () => {
    const isInitial = newAccountData?.isInitialSetup ?? false;
    setFormData({ name: '', email: '', password: '', confirmPassword: '', verificationCode: '', referredBy: '' });
    setVerificationToken('');
    setSetupStep('selection');
    setNewAccountData(null);
    setReferralNotFound(false);
    setAgreedToTerms(false);
    setShowAgreement(false);
    if (isInitial && onAccountSetupComplete) {
      onAccountSetupComplete();
    } else {
      if (onBack) onBack();
    }
  };

  const handleReferralSubmit = async () => {
    if (!formData.referredBy.trim()) {
      setErrors({ referredBy: 'Please enter a referral code or email, or tap Skip' });
      return;
    }
    if (!storage || !apiService) {
      Alert.alert('Error', 'Service not available');
      return;
    }
    setIsLoading(true);
    setReferralNotFound(false);
    setErrors({});
    try {
      const userSignature = await storage.getItem('user_signature') || API_CONSTANTS.ACCOUNT_SERVICE_SIGNATURE;
      const userSraId = await storage.getItem('user_sra_id') || '';
      const referralPayload = {
        referredBy: formData.referredBy.trim(),
        sraId: userSraId,
        signature: userSignature,
        accesstype: API_CONSTANTS.DEFAULT_ACCESS_TYPE
      };
      const serviceRequest = {
        RequestType: 'signup_request_referral',
        Payload: JSON.stringify(referralPayload)
      };
      console.log('🔗 Submitting referral:', serviceRequest.RequestType);
      const serviceResponse = await apiService.accountService(serviceRequest);
      if (serviceResponse?.success === true && !serviceResponse?.errorCode && !serviceResponse?.errorDescription) {
        completeAccountSetup();
      } else {
        setReferralNotFound(true);
      }
    } catch (error: any) {
      console.error('❌ Referral submission error:', error);
      setReferralNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReferralSkip = () => {
    completeAccountSetup();
  };

  const renderReferralScreen = () => (
    <View style={styles.container}>
      <View style={styles.backgroundImageContainer}>
        <View style={styles.backgroundOverlay} />
      </View>
      <View style={styles.formContent}>
        <TouchableOpacity style={styles.backButton} onPress={handleReferralSkip}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>

        <Text style={styles.formTitle}>Were You Referred?</Text>
        <Text style={styles.formSubtitle}>
          Help the person who referred you earn Gift Rewards! Enter their email or account code below to give them credit for your sign up.
        </Text>

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Referred by (optional)</Text>
            <TextInput
              style={[styles.input, errors.referredBy ? styles.inputError : null]}
              value={formData.referredBy}
              onChangeText={(text) => {
                setFormData({ ...formData, referredBy: text });
                setReferralNotFound(false);
                setErrors({});
              }}
              placeholder="Referring email or account code"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isLoading}
            />
            {errors.referredBy && <Text style={styles.errorText}>{errors.referredBy}</Text>}
          </View>

          {referralNotFound && (
            <View style={styles.referralNotFoundBox}>
              <Text style={styles.referralNotFoundText}>
                ⚠️ That code or email was not found. Please check and try again, or tap Skip to continue without a referral.
              </Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.submitButton, isLoading && styles.submitButtonDisabled]}
            onPress={handleReferralSubmit}
            disabled={isLoading}
          >
            <Text style={styles.submitButtonText}>
              {isLoading ? 'Submitting...' : 'Submit'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.skipButton, isLoading && styles.submitButtonDisabled]}
            onPress={handleReferralSkip}
            disabled={isLoading}
          >
            <Text style={styles.skipButtonText}>Skip</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  // Main render logic
  if (setupStep === 'selection') {
    return renderSelectionScreen();
  } else if (setupStep === 'referral') {
    return renderReferralScreen();
  } else {
    return renderFormScreen();
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#3b82f6',
    alignItems: 'center',
  },
  backgroundImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#3b82f6',
  },
  backgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
  },
  welcomeContent: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
    zIndex: 1,
    width: '100%',
    maxWidth: 750,
  },
  formContent: {
    flex: 1,
    padding: 24,
    zIndex: 1,
    width: '100%',
    maxWidth: 750,
  },
  backButton: {
    alignSelf: 'flex-start',
    marginBottom: 20,
  },
  backButtonTop: {
    position: 'absolute',
    top: 48,
    left: 24,
    zIndex: 2,
    paddingVertical: 8,
    paddingHorizontal: 0,
  },
  backButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  imageSection: {
    alignItems: 'center',
    marginBottom: 32,
  },
  colorfulImagePlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  imageEmojis: {
    fontSize: 16,
    lineHeight: 20,
    textAlign: 'center',
  },
  welcomeTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 16,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  welcomeSubtitle: {
    fontSize: 16,
    color: '#e2e8f0',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 40,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  optionsContainer: {
    gap: 16,
  },
  currentAccountCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 12,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  currentAccountIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  currentAccountInfo: {
    flex: 1,
  },
  currentAccountTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 4,
  },
  currentAccountEmail: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '500',
  },
  currentAccountLastLogin: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 2,
  },
  signOutButton: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  signOutButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  optionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 12,
    padding: 20,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  optionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  optionDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    lineHeight: 20,
  },
  formTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  formSubtitle: {
    fontSize: 16,
    color: '#e2e8f0',
    lineHeight: 24,
    marginBottom: 32,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  form: {
    gap: 20,
  },
  inputContainer: {
    marginBottom: 4,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  input: {
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#1f2937',
  },
  inputError: {
    borderColor: '#fca5a5',
    backgroundColor: 'rgba(254, 226, 226, 0.9)',
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  passwordInput: {
    flex: 1,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#1f2937',
  },
  eyeButton: {
    position: 'absolute',
    right: 12,
    padding: 4,
  },
  eyeButtonText: {
    fontSize: 18,
  },
  errorText: {
    color: '#fca5a5',
    fontSize: 14,
    marginTop: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  submitButton: {
    backgroundColor: '#10b981',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 24,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  submitButtonDisabled: {
    backgroundColor: '#95a5a6',
    opacity: 0.7,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  referralInfoBox: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 10,
    padding: 16,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  referralInfoTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 8,
  },
  referralInfoText: {
    fontSize: 13,
    color: '#e2e8f0',
    lineHeight: 20,
    marginBottom: 6,
  },
  referralNotFoundBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.5)',
  },
  referralNotFoundText: {
    fontSize: 14,
    color: '#fca5a5',
    lineHeight: 20,
  },
  skipButton: {
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    marginTop: 4,
  },
  skipButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 4,
    marginBottom: 4,
  },
  termsCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.7)',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
    flexShrink: 0,
  },
  termsCheckboxChecked: {
    backgroundColor: '#10b981',
    borderColor: '#10b981',
  },
  termsCheckboxMark: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
  },
  termsText: {
    fontSize: 14,
    color: '#e2e8f0',
    flex: 1,
    lineHeight: 20,
  },
  termsLink: {
    color: '#93c5fd',
    textDecorationLine: 'underline',
  },
});